import { db } from './firebase';
import { collection, addDoc, updateDoc, doc, getDoc, query, where, getDocs } from 'firebase/firestore';

// Store order in Firebase
export const storeOrderInFirebase = async (orderData) => {
  try {
    const ordersRef = collection(db, 'orders');
    const docRef = await addDoc(ordersRef, {
      ...orderData,
      createdAt: new Date(),
      status: 'placed',
      updatedAt: new Date()
    });
    console.log('Order stored successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error storing order:', error);
    throw error;
  }
};

// Update product stock after order
export const updateProductStock = async (cartItems) => {
  try {
    console.log('Starting stock update for cart items:', cartItems);

    for (const item of cartItems) {
      console.log('Processing item:', item);
      
      // Get the product document
      const productRef = doc(db, 'products', item.productID);
      const productDoc = await getDoc(productRef);
      
      if (productDoc.exists()) {
        const productData = productDoc.data();
        console.log('Found product:', productData.name || productData.PName);
        
        // Find the correct variant and size to update
        // Since sole type is not stored in DB, we match by color and size only
        if (productData.PType && Array.isArray(productData.PType)) {
          const updatedPType = productData.PType.map(variant => {
            // Check if this variant matches the ordered item by color
            // We ignore sole type since it's not stored in the database
            const variantColor = variant.PSoleColor || variant.PTypeName || 'Default';
            const itemColor = item.productColor;
            
            if (variantColor === itemColor) {
              console.log('Color match found, updating stock...');
              // Update the stock for the specific size
              if (variant.PStock && Array.isArray(variant.PStock)) {
                const updatedPStock = variant.PStock.map(stock => {
                  if (stock.Size === item.productSize) {
                    const newQty = Math.max(0, (stock.Qty || 0) - item.productQuantity);
                    console.log(`Updating size ${item.productSize} from ${stock.Qty} to ${newQty}`);
                    return { ...stock, Qty: newQty };
                  }
                  return stock;
                });
                return { ...variant, PStock: updatedPStock };
              }
            }
            return variant;
          });
          
          // Update the product in Firebase
          await updateDoc(productRef, {
            PType: updatedPType,
            updatedAt: new Date()
          });
          console.log('Product stock updated successfully for:', productData.name || productData.PName);
        } else {
          console.log('No PType array found in product data');
        }
      } else {
        console.log('Product document not found for ID:', item.productID);
      }
    }
    
    console.log('All product stock updates completed');
  } catch (error) {
    console.error('Error updating product stock:', error);
    throw error;
  }
};

// Helper function to generate variant ID (for frontend use only)
// This is kept for consistency but not used in stock updates
export const generateVariantId = (variant, size, soleType = 'Leather') => {
  if (variant.id) {
    return `${variant.id}-${size}-${soleType}`;
  }
  const color = variant.PSoleColor || variant.PTypeName || 'Default';
  return `${color.replace(/\s/g, '_')}-${size}-${soleType}`;
};

// Get all orders for a user by email
export const getUserOrders = async (userEmail) => {
  try {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, where('customer.email', '==', userEmail));
    const querySnapshot = await getDocs(q);
    
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort orders by date (newest first)
    orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return orders;
  } catch (error) {
    console.error('Error getting user orders:', error);
    throw error;
  }
};

// Get order by ID
export const getOrderById = async (orderId) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (orderDoc.exists()) {
      return {
        id: orderDoc.id,
        ...orderDoc.data()
      };
    } else {
      throw new Error('Order not found');
    }
  } catch (error) {
    console.error('Error getting order:', error);
    throw error;
  }
};

// Update order status
export const updateOrderStatus = async (orderId, newStatus) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      status: newStatus,
      updatedAt: new Date()
    });
    console.log(`Order ${orderId} status updated to: ${newStatus}`);
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// Get all orders (for admin)
export const getAllOrders = async () => {
  try {
    const ordersRef = collection(db, 'orders');
    const querySnapshot = await getDocs(ordersRef);
    
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort orders by date (newest first)
    orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return orders;
  } catch (error) {
    console.error('Error getting all orders:', error);
    throw error;
  }
};

// Cancel order and restore stock
export const cancelOrderAndRestoreStock = async (orderId) => {
  try {
    const order = await getOrderById(orderId);
    
    if (!order) {
      throw new Error('Order not found');
    }

    // Restore product stock
    await restoreProductStock(order.items);

    // Update order status to cancelled
    await updateOrderStatus(orderId, 'cancelled');

    console.log(`Order ${orderId} cancelled and stock restored`);
  } catch (error) {
    console.error('Error cancelling order and restoring stock:', error);
    throw error;
  }
};

// Restore product stock (reverse of updateProductStock)
const restoreProductStock = async (cartItems) => {
  try {
    console.log('Restoring stock for items:', cartItems);

    for (const item of cartItems) {
      // Get the product document
      const productRef = doc(db, 'products', item.productID);
      const productDoc = await getDoc(productRef);
      
      if (productDoc.exists()) {
        const productData = productDoc.data();
        
        // Find the correct variant and size to restore stock
        // Match by color and size only (sole type not stored in DB)
        if (productData.PType && Array.isArray(productData.PType)) {
          const updatedPType = productData.PType.map(variant => {
            const variantColor = variant.PSoleColor || variant.PTypeName || 'Default';
            const itemColor = item.productColor;
            
            if (variantColor === itemColor) {
              // Restore the stock for the specific size
              if (variant.PStock && Array.isArray(variant.PStock)) {
                const updatedPStock = variant.PStock.map(stock => {
                  if (stock.Size === item.productSize) {
                    const newQty = (stock.Qty || 0) + item.productQuantity;
                    console.log(`Restoring size ${item.productSize} from ${stock.Qty} to ${newQty}`);
                    return { ...stock, Qty: newQty };
                  }
                  return stock;
                });
                return { ...variant, PStock: updatedPStock };
              }
            }
            return variant;
          });
          
          // Update the product in Firebase
          await updateDoc(productRef, {
            PType: updatedPType,
            updatedAt: new Date()
          });
          console.log('Product stock restored for:', productData.name || productData.PName);
        }
      }
    }
    
    console.log('All product stock restorations completed');
  } catch (error) {
    console.error('Error restoring product stock:', error);
    throw error;
  }
};

// Get low stock products (for admin alerts)
export const getLowStockProducts = async (threshold = 5) => {
  try {
    const productsRef = collection(db, 'products');
    const querySnapshot = await getDocs(productsRef);
    
    const lowStockProducts = [];
    
    querySnapshot.forEach((doc) => {
      const productData = doc.data();
      let totalStock = 0;
      let lowStockVariants = [];
      
      if (productData.PType && Array.isArray(productData.PType)) {
        productData.PType.forEach(variant => {
          if (variant.PStock && Array.isArray(variant.PStock)) {
            variant.PStock.forEach(stock => {
              totalStock += stock.Qty || 0;
              if (stock.Qty <= threshold) {
                lowStockVariants.push({
                  color: variant.PSoleColor || variant.PTypeName || 'Default',
                  size: stock.Size,
                  stock: stock.Qty
                });
              }
            });
          }
        });
      }
      
      if (lowStockVariants.length > 0) {
        lowStockProducts.push({
          id: doc.id,
          name: productData.name || productData.PName,
          totalStock,
          lowStockVariants
        });
      }
    });
    
    return lowStockProducts;
  } catch (error) {
    console.error('Error getting low stock products:', error);
    throw error;
  }
};